/**
 * Color theory, lighting design, mood, and holiday palettes for expert scene generation.
 * All generators accept a base {r,g,b} and fixture count, returning RGB arrays or look sequences.
 */

import { SPORTS_GENERATORS, SPORTS_CATEGORIES } from './sportsColorPresets';

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

export function rgbToHsl(r, g, b) {
	r /= 255;
	g /= 255;
	b /= 255;
	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	let h = 0;
	let s = 0;
	const l = (max + min) / 2;
	if (max !== min) {
		const d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
		switch (max) {
			case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
			case g: h = ((b - r) / d + 2) / 6; break;
			default: h = ((r - g) / d + 4) / 6; break;
		}
	}
	return { h: h * 360, s: s * 100, l: l * 100 };
}

export function hslToRgb(h, s, l) {
	h = ((h % 360) + 360) % 360;
	s = clamp(s, 0, 100) / 100;
	l = clamp(l, 0, 100) / 100;
	const c = (1 - Math.abs(2 * l - 1)) * s;
	const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
	const m = l - c / 2;
	let r = 0;
	let g = 0;
	let b = 0;
	if (h < 60) { r = c; g = x; }
	else if (h < 120) { r = x; g = c; }
	else if (h < 180) { g = c; b = x; }
	else if (h < 240) { g = x; b = c; }
	else if (h < 300) { r = x; b = c; }
	else { r = c; b = x; }
	return {
		r: Math.round((r + m) * 255),
		g: Math.round((g + m) * 255),
		b: Math.round((b + m) * 255),
	};
}

export function rgbToHsv(r, g, b) {
	r /= 255;
	g /= 255;
	b /= 255;
	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	const d = max - min;
	let h = 0;
	if (d !== 0) {
		switch (max) {
			case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
			case g: h = ((b - r) / d + 2) / 6; break;
			default: h = ((r - g) / d + 4) / 6; break;
		}
	}
	const s = max === 0 ? 0 : d / max;
	const v = max;
	return { h: h * 360, s: s * 100, v: v * 100 };
}

export function hsvToRgb(h, s, v) {
	h = ((h % 360) + 360) % 360;
	s = clamp(s, 0, 100) / 100;
	v = clamp(v, 0, 100) / 100;
	const c = v * s;
	const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
	const m = v - c;
	let r = 0;
	let g = 0;
	let b = 0;
	if (h < 60) { r = c; g = x; }
	else if (h < 120) { r = x; g = c; }
	else if (h < 180) { g = c; b = x; }
	else if (h < 240) { g = x; b = c; }
	else if (h < 300) { r = x; b = c; }
	else { r = c; b = x; }
	return {
		r: Math.round((r + m) * 255),
		g: Math.round((g + m) * 255),
		b: Math.round((b + m) * 255),
	};
}

export function rgb(r, g, b) {
	return { r: clamp(Math.round(r), 0, 255), g: clamp(Math.round(g), 0, 255), b: clamp(Math.round(b), 0, 255) };
}

function fromHsl(h, s, l) {
	return hslToRgb(h, s, l);
}

function fromBase(base, hOffset = 0, sMul = 1, lOffset = 0) {
	const { h, s, l } = rgbToHsl(base.r, base.g, base.b);
	return fromHsl(h + hOffset, clamp(s * sMul, 8, 100), clamp(l + lOffset, 5, 95));
}

function rotateHue(base, degrees) {
	return fromBase(base, degrees, 1, 0);
}

function varyLightness(base, offsets) {
	const { h, s, l } = rgbToHsl(base.r, base.g, base.b);
	return offsets.map((off) => fromHsl(h, s, clamp(l + off, 5, 95)));
}

function fitCount(colors, count) {
	if (!count || count <= 0) return colors;
	const out = [];
	for (let i = 0; i < count; i++) {
		out.push({ ...colors[i % colors.length] });
	}
	return out;
}

function singleLook(colors, count) {
	return { type: 'look', colors: fitCount(colors, count) };
}

function sceneLooks(looks, count, hold = 30) {
	return {
		type: 'cycle',
		looks: looks.map((colors) => ({
			colors: fitCount(colors, count),
			hold,
		})),
	};
}

// --- Generators keyed by method id ---

function genComplementary(base, n) {
	return singleLook([base, rotateHue(base, 180)], n);
}

function genSplitComplementary(base, n) {
	return singleLook([base, rotateHue(base, 150), rotateHue(base, 210)], n);
}

function genTriadic(base, n) {
	return singleLook([base, rotateHue(base, 120), rotateHue(base, 240)], n);
}

function genTetradic(base, n) {
	return singleLook([base, rotateHue(base, 90), rotateHue(base, 180), rotateHue(base, 270)], n);
}

function genAnalogous(base, n) {
	return singleLook([rotateHue(base, -30), base, rotateHue(base, 30), rotateHue(base, 60)], n);
}

function genAccentedAnalogous(base, n) {
	return singleLook([rotateHue(base, -30), base, rotateHue(base, 30), rotateHue(base, 180)], n);
}

function genMonochromatic(base, n) {
	return singleLook(varyLightness(base, [25, 10, 0, -15]), n);
}

function genMonochromaticDeep(base, n) {
	return singleLook(varyLightness(base, [-20, -10, 0, 8]), n);
}

function genWarmCoolMcCandless(base, n) {
	const warm = fromHsl(28, 85, 55);
	const cool = fromHsl(210, 70, 45);
	return singleLook([warm, warm, cool, cool].map((c, i) => (i < 2 ? fromBase(base, -10, 1.1, 5) : rotateHue(base, 180))), n);
}

function genHighKey(base, n) {
	return singleLook(varyLightness(base, [30, 25, 20, 15]), n);
}

function genLowKey(base, n) {
	return singleLook(varyLightness(base, [-25, -20, -15, -10]), n);
}

function genFrontWarmBackCool(base, n) {
	return singleLook([
		fromHsl(35, 80, 60),
		fromHsl(30, 75, 55),
		fromHsl(215, 55, 35),
		fromHsl(220, 50, 30),
	], n);
}

function genAmberWashBlueBack(base, n) {
	return singleLook([
		rgb(255, 180, 80),
		rgb(255, 160, 60),
		rgb(60, 100, 200),
		rgb(40, 70, 160),
	], n);
}

function genSidelightContrast(base, n) {
	return singleLook([
		fromBase(base, 0, 1, 15),
		fromBase(base, 0, 0.6, -20),
		fromBase(base, 180, 0.8, 5),
		fromBase(base, 180, 0.5, -25),
	], n);
}

function genGel3200K(base, n) {
	return singleLook([rgb(255, 210, 160), rgb(255, 195, 140), rgb(255, 180, 120), rgb(240, 165, 100)], n);
}

function genGel5600K(base, n) {
	return singleLook([rgb(255, 250, 240), rgb(240, 245, 255), rgb(220, 235, 255), rgb(200, 220, 250)], n);
}

function genFire(base, n) {
	return sceneLooks([
		[rgb(255, 60, 0), rgb(255, 120, 0), rgb(200, 20, 0), rgb(255, 180, 40)],
		[rgb(255, 100, 0), rgb(255, 40, 0), rgb(255, 160, 20), rgb(180, 30, 0)],
		[rgb(255, 140, 20), rgb(255, 80, 0), rgb(220, 50, 0), rgb(255, 200, 60)],
	], n);
}

function genOcean(base, n) {
	return singleLook([rgb(0, 80, 140), rgb(0, 120, 180), rgb(20, 160, 200), rgb(0, 60, 100)], n);
}

function genSunset(base, n) {
	return sceneLooks([
		[rgb(255, 120, 60), rgb(255, 80, 100), rgb(140, 60, 160), rgb(60, 40, 120)],
		[rgb(255, 90, 40), rgb(255, 60, 80), rgb(120, 50, 140), rgb(40, 30, 100)],
		[rgb(255, 60, 20), rgb(200, 40, 60), rgb(80, 30, 100), rgb(20, 15, 60)],
	], n);
}

function genForest(base, n) {
	return singleLook([rgb(30, 90, 40), rgb(50, 120, 50), rgb(80, 100, 40), rgb(40, 60, 30)], n);
}

function genMoonlight(base, n) {
	return singleLook([rgb(120, 150, 220), rgb(80, 110, 180), rgb(60, 90, 160), rgb(40, 60, 120)], n);
}

function genCandlelight(base, n) {
	return singleLook([rgb(255, 180, 100), rgb(255, 150, 70), rgb(255, 120, 50), rgb(200, 90, 40)], n);
}

function genNeonCyberpunk(base, n) {
	return singleLook([rgb(255, 0, 128), rgb(0, 255, 255), rgb(180, 0, 255), rgb(0, 200, 255)], n);
}

function genAurora(base, n) {
	return sceneLooks([
		[rgb(0, 255, 120), rgb(0, 180, 255), rgb(120, 0, 255), rgb(0, 220, 180)],
		[rgb(0, 200, 100), rgb(0, 140, 220), rgb(100, 0, 220), rgb(0, 180, 160)],
		[rgb(0, 160, 80), rgb(0, 100, 180), rgb(80, 0, 180), rgb(0, 140, 140)],
	], n);
}

function genStorm(base, n) {
	return singleLook([rgb(60, 70, 90), rgb(80, 90, 120), rgb(100, 80, 140), rgb(40, 50, 70)], n);
}

function genRoyal(base, n) {
	return singleLook([rgb(80, 20, 120), rgb(120, 40, 160), rgb(200, 170, 40), rgb(160, 120, 30)], n);
}

function genSpringGarden(base, n) {
	return singleLook([rgb(180, 255, 180), rgb(255, 180, 220), rgb(255, 240, 150), rgb(200, 255, 200)], n);
}

function genAutumnHarvest(base, n) {
	return singleLook([rgb(200, 100, 30), rgb(180, 60, 20), rgb(220, 160, 40), rgb(120, 50, 20)], n);
}

function genWinterFrost(base, n) {
	return singleLook([rgb(220, 240, 255), rgb(180, 210, 240), rgb(200, 230, 250), rgb(150, 190, 230)], n);
}

function genUnderwater(base, n) {
	return singleLook([rgb(0, 100, 120), rgb(0, 140, 160), rgb(20, 80, 100), rgb(0, 60, 80)], n);
}

function genDesertDusk(base, n) {
	return singleLook([rgb(200, 120, 80), rgb(180, 90, 60), rgb(140, 70, 90), rgb(100, 50, 70)], n);
}

function genLava(base, n) {
	return sceneLooks([
		[rgb(255, 40, 0), rgb(200, 0, 0), rgb(255, 100, 0), rgb(120, 0, 0)],
		[rgb(255, 80, 0), rgb(255, 20, 0), rgb(255, 140, 20), rgb(180, 10, 0)],
	], n);
}

function genIcePalace(base, n) {
	return singleLook([rgb(180, 230, 255), rgb(140, 200, 255), rgb(100, 180, 240), rgb(220, 245, 255)], n);
}

function genTropical(base, n) {
	return singleLook([rgb(0, 200, 180), rgb(255, 220, 0), rgb(255, 100, 150), rgb(0, 180, 120)], n);
}

function genBlacklight(base, n) {
	return singleLook([rgb(120, 0, 255), rgb(0, 255, 120), rgb(255, 0, 200), rgb(0, 200, 255)], n);
}

function genSepiaVintage(base, n) {
	return singleLook([rgb(180, 140, 100), rgb(160, 120, 80), rgb(200, 160, 120), rgb(140, 100, 70)], n);
}

function genPastelDream(base, n) {
	return singleLook([rgb(255, 200, 220), rgb(200, 220, 255), rgb(220, 255, 200), rgb(255, 230, 200)], n);
}

function genBoldPrimary(base, n) {
	return singleLook([rgb(255, 0, 0), rgb(0, 200, 0), rgb(0, 100, 255), rgb(255, 220, 0)], n);
}

function genDawn(base, n) {
	return sceneLooks([
		[rgb(40, 30, 80), rgb(80, 50, 120), rgb(120, 60, 100), rgb(60, 40, 90)],
		[rgb(255, 160, 120), rgb(255, 120, 100), rgb(200, 100, 140), rgb(140, 80, 120)],
		[rgb(255, 200, 160), rgb(200, 220, 255), rgb(180, 210, 240), rgb(255, 180, 140)],
	], n);
}

function genGoldenHour(base, n) {
	return singleLook([rgb(255, 200, 120), rgb(255, 160, 80), rgb(255, 140, 60), rgb(220, 120, 50)], n);
}

function genBlueHour(base, n) {
	return singleLook([rgb(30, 50, 100), rgb(50, 70, 130), rgb(80, 100, 160), rgb(255, 120, 80)], n);
}

function genTwilight(base, n) {
	return sceneLooks([
		[rgb(255, 100, 60), rgb(180, 60, 100), rgb(80, 40, 120), rgb(40, 30, 80)],
		[rgb(60, 50, 100), rgb(40, 40, 80), rgb(30, 35, 70), rgb(20, 25, 60)],
	], n);
}

function genMiddaySun(base, n) {
	return singleLook([rgb(255, 250, 230), rgb(255, 245, 210), rgb(255, 240, 200), rgb(250, 235, 190)], n);
}

function genOvercast(base, n) {
	return singleLook([rgb(160, 170, 185), rgb(140, 150, 165), rgb(180, 185, 195), rgb(120, 130, 145)], n);
}

function genRain(base, n) {
	return sceneLooks([
		[rgb(80, 90, 110), rgb(60, 70, 90), rgb(100, 110, 130), rgb(50, 60, 80)],
		[rgb(70, 80, 100), rgb(90, 100, 120), rgb(55, 65, 85), rgb(75, 85, 105)],
	], n);
}

function genLightningStorm(base, n) {
	return sceneLooks([
		[rgb(30, 35, 50), rgb(40, 45, 65), rgb(25, 30, 45), rgb(35, 40, 55)],
		[rgb(200, 210, 230), rgb(40, 45, 65), rgb(30, 35, 50), rgb(180, 190, 210)],
		[rgb(30, 35, 50), rgb(35, 40, 55), rgb(220, 225, 240), rgb(25, 30, 45)],
	], n);
}

function genFogMist(base, n) {
	return singleLook([rgb(200, 210, 215), rgb(180, 190, 200), rgb(220, 225, 230), rgb(160, 175, 185)], n);
}

function genSnowfall(base, n) {
	return sceneLooks([
		[rgb(220, 230, 240), rgb(200, 215, 230), rgb(240, 245, 250), rgb(190, 205, 220)],
		[rgb(210, 220, 235), rgb(230, 238, 245), rgb(195, 210, 225), rgb(215, 225, 238)],
	], n);
}

function genBlizzard(base, n) {
	return singleLook([rgb(240, 245, 250), rgb(200, 210, 225), rgb(255, 255, 255), rgb(180, 195, 210)], n);
}

function genJungle(base, n) {
	return singleLook([rgb(10, 80, 30), rgb(20, 100, 40), rgb(30, 70, 25), rgb(15, 60, 20)], n);
}

function genSwamp(base, n) {
	return singleLook([rgb(40, 60, 30), rgb(60, 50, 25), rgb(30, 45, 35), rgb(80, 70, 40)], n);
}

function genSavanna(base, n) {
	return singleLook([rgb(220, 180, 80), rgb(180, 140, 60), rgb(140, 100, 50), rgb(255, 200, 100)], n);
}

function genCave(base, n) {
	return singleLook([rgb(255, 140, 60), rgb(200, 100, 40), rgb(40, 30, 20), rgb(255, 180, 80)], n);
}

function genVolcanicAsh(base, n) {
	return singleLook([rgb(60, 55, 50), rgb(120, 40, 30), rgb(80, 70, 65), rgb(100, 30, 20)], n);
}

function genDeepSpace(base, n) {
	return singleLook([rgb(5, 5, 20), rgb(20, 10, 60), rgb(10, 20, 80), rgb(40, 20, 100)], n);
}

function genStarlight(base, n) {
	return singleLook([rgb(15, 20, 45), rgb(25, 35, 70), rgb(10, 15, 35), rgb(30, 40, 80)], n);
}

function genIndustrial(base, n) {
	return singleLook([rgb(255, 180, 60), rgb(200, 140, 40), rgb(100, 100, 110), rgb(80, 85, 95)], n);
}

function genSodiumVapor(base, n) {
	return singleLook([rgb(255, 200, 80), rgb(255, 180, 50), rgb(230, 160, 40), rgb(200, 140, 30)], n);
}

function genFluorescent(base, n) {
	return singleLook([rgb(240, 255, 240), rgb(220, 240, 230), rgb(200, 230, 220), rgb(230, 250, 235)], n);
}

function genClinical(base, n) {
	return singleLook([rgb(230, 245, 250), rgb(210, 235, 245), rgb(200, 230, 240), rgb(220, 240, 248)], n);
}

function genNightclub(base, n) {
	return sceneLooks([
		[rgb(255, 0, 100), rgb(0, 200, 255), rgb(180, 0, 255), rgb(255, 255, 255)],
		[rgb(0, 255, 150), rgb(255, 50, 0), rgb(0, 100, 255), rgb(255, 0, 200)],
		[rgb(255, 255, 0), rgb(0, 0, 255), rgb(255, 0, 0), rgb(0, 255, 255)],
	], n);
}

function genConcertStage(base, n) {
	return sceneLooks([
		[rgb(200, 0, 0), rgb(80, 0, 0), rgb(255, 60, 0), rgb(40, 0, 0)],
		[rgb(255, 100, 0), rgb(200, 0, 50), rgb(255, 200, 100), rgb(120, 0, 30)],
	], n);
}

function genHorror(base, n) {
	return singleLook([rgb(80, 160, 40), rgb(40, 80, 20), rgb(120, 0, 0), rgb(20, 40, 10)], n);
}

function genHaunted(base, n) {
	return sceneLooks([
		[rgb(40, 60, 30), rgb(60, 40, 80), rgb(30, 50, 25), rgb(80, 60, 100)],
		[rgb(50, 70, 35), rgb(70, 50, 90), rgb(100, 80, 120), rgb(35, 55, 30)],
	], n);
}

function genSciFiBridge(base, n) {
	return singleLook([rgb(0, 180, 200), rgb(0, 120, 160), rgb(0, 80, 120), rgb(40, 200, 220)], n);
}

function genFantasyEnchanted(base, n) {
	return sceneLooks([
		[rgb(160, 80, 220), rgb(255, 220, 100), rgb(120, 60, 180), rgb(255, 180, 220)],
		[rgb(100, 200, 255), rgb(200, 100, 255), rgb(255, 200, 100), rgb(180, 120, 255)],
	], n);
}

function genUnderworld(base, n) {
	return singleLook([rgb(120, 0, 0), rgb(40, 0, 0), rgb(200, 60, 0), rgb(20, 0, 0)], n);
}

function genEthereal(base, n) {
	return singleLook([rgb(255, 250, 240), rgb(255, 230, 210), rgb(255, 240, 255), rgb(240, 250, 255)], n);
}

function genApocalypse(base, n) {
	return singleLook([rgb(140, 100, 60), rgb(100, 80, 50), rgb(180, 60, 30), rgb(80, 70, 55)], n);
}

function genBioluminescence(base, n) {
	return sceneLooks([
		[rgb(0, 200, 180), rgb(0, 80, 120), rgb(0, 255, 200), rgb(0, 60, 100)],
		[rgb(0, 160, 160), rgb(0, 220, 190), rgb(0, 100, 140), rgb(0, 180, 170)],
	], n);
}

function genCoralReef(base, n) {
	return singleLook([rgb(255, 120, 100), rgb(0, 200, 180), rgb(255, 180, 80), rgb(255, 80, 120)], n);
}

function genArcticTundra(base, n) {
	return singleLook([rgb(230, 240, 250), rgb(180, 200, 220), rgb(140, 170, 200), rgb(200, 220, 235)], n);
}

function genMediterranean(base, n) {
	return singleLook([rgb(0, 150, 200), rgb(255, 255, 255), rgb(200, 100, 60), rgb(0, 120, 170)], n);
}

function genCherryBlossom(base, n) {
	return singleLook([rgb(255, 190, 210), rgb(255, 220, 230), rgb(200, 240, 200), rgb(255, 180, 200)], n);
}

function genWineCellar(base, n) {
	return singleLook([rgb(80, 10, 30), rgb(120, 20, 40), rgb(200, 120, 60), rgb(60, 5, 20)], n);
}

function genLanternFestival(base, n) {
	return sceneLooks([
		[rgb(255, 160, 40), rgb(255, 120, 20), rgb(255, 200, 80), rgb(220, 100, 30)],
		[rgb(255, 180, 60), rgb(255, 140, 40), rgb(255, 100, 10), rgb(240, 160, 50)],
	], n);
}

function genCampfire(base, n) {
	return sceneLooks([
		[rgb(255, 120, 40), rgb(255, 80, 20), rgb(200, 40, 0), rgb(255, 160, 60)],
		[rgb(255, 100, 30), rgb(255, 140, 50), rgb(220, 60, 10), rgb(255, 90, 25)],
		[rgb(255, 130, 45), rgb(240, 70, 15), rgb(255, 170, 70), rgb(210, 50, 5)],
	], n);
}

function genSmogHaze(base, n) {
	return singleLook([rgb(160, 140, 110), rgb(140, 120, 95), rgb(180, 160, 130), rgb(120, 100, 80)], n);
}

function genWizardStudy(base, n) {
	return singleLook([rgb(80, 40, 120), rgb(255, 160, 80), rgb(60, 30, 90), rgb(200, 140, 60)], n);
}

function genAbandonedWarehouse(base, n) {
	return singleLook([rgb(60, 70, 55), rgb(80, 85, 70), rgb(40, 45, 35), rgb(100, 105, 85)], n);
}

function genEmeraldCity(base, n) {
	return singleLook([rgb(0, 180, 80), rgb(0, 140, 60), rgb(180, 220, 200), rgb(0, 200, 100)], n);
}

function genInfrared(base, n) {
	return singleLook([rgb(255, 0, 0), rgb(200, 0, 0), rgb(255, 80, 0), rgb(160, 0, 0)], n);
}

function genRaveStrobe(base, n) {
	return sceneLooks([
		[rgb(20, 20, 30), rgb(20, 20, 30), rgb(20, 20, 30), rgb(255, 255, 255)],
		[rgb(255, 255, 255), rgb(30, 30, 40), rgb(30, 30, 40), rgb(30, 30, 40)],
	], n);
}

function genDeepOcean(base, n) {
	return singleLook([rgb(0, 20, 60), rgb(0, 40, 80), rgb(0, 10, 40), rgb(0, 30, 70)], n);
}

function genShallowPool(base, n) {
	return singleLook([rgb(100, 220, 230), rgb(80, 200, 210), rgb(120, 240, 245), rgb(60, 180, 200)], n);
}

function genMarshland(base, n) {
	return singleLook([rgb(70, 90, 50), rgb(90, 80, 45), rgb(50, 70, 40), rgb(100, 95, 55)], n);
}

function genHighNoonDesert(base, n) {
	return singleLook([rgb(255, 230, 180), rgb(240, 200, 140), rgb(220, 180, 120), rgb(255, 240, 200)], n);
}

function genNorthernWoods(base, n) {
	return singleLook([rgb(30, 50, 35), rgb(50, 70, 45), rgb(20, 40, 30), rgb(60, 80, 50)], n);
}

function genCopperMine(base, n) {
	return singleLook([rgb(180, 100, 60), rgb(140, 80, 50), rgb(100, 60, 40), rgb(200, 120, 70)], n);
}

function genSteelMill(base, n) {
	return sceneLooks([
		[rgb(255, 100, 0), rgb(80, 80, 85), rgb(200, 80, 20), rgb(60, 60, 65)],
		[rgb(255, 140, 40), rgb(100, 100, 105), rgb(180, 60, 10), rgb(70, 70, 75)],
	], n);
}

function genTrafficSignal(base, n) {
	return sceneLooks([
		[rgb(200, 0, 0), rgb(40, 40, 40), rgb(40, 40, 40), rgb(40, 40, 40)],
		[rgb(40, 40, 40), rgb(255, 180, 0), rgb(40, 40, 40), rgb(40, 40, 40)],
		[rgb(40, 40, 40), rgb(40, 40, 40), rgb(0, 160, 0), rgb(40, 40, 40)],
	], n);
}

function genPoliceSiren(base, n) {
	return sceneLooks([
		[rgb(200, 0, 0), rgb(0, 0, 200), rgb(200, 0, 0), rgb(0, 0, 200)],
		[rgb(0, 0, 200), rgb(200, 0, 0), rgb(0, 0, 200), rgb(200, 0, 0)],
	], n);
}

function genTelevisionGlow(base, n) {
	return sceneLooks([
		[rgb(180, 200, 220), rgb(160, 180, 200), rgb(170, 190, 210), rgb(150, 170, 190)],
		[rgb(200, 210, 230), rgb(170, 185, 205), rgb(190, 200, 220), rgb(160, 175, 195)],
	], n);
}

function genMoodyNoir(base, n) {
	return singleLook([rgb(20, 20, 25), rgb(40, 35, 45), rgb(60, 50, 55), rgb(30, 28, 35)], n);
}

function genRomanticDim(base, n) {
	return singleLook([rgb(180, 60, 80), rgb(120, 30, 50), rgb(255, 160, 140), rgb(80, 20, 40)], n);
}

function genZenGarden(base, n) {
	return singleLook([rgb(180, 190, 175), rgb(160, 170, 155), rgb(200, 195, 180), rgb(140, 150, 135)], n);
}

function genHotSpring(base, n) {
	return sceneLooks([
		[rgb(200, 230, 220), rgb(180, 210, 200), rgb(220, 240, 235), rgb(160, 200, 190)],
		[rgb(190, 220, 210), rgb(210, 235, 225), rgb(170, 205, 195), rgb(200, 225, 215)],
	], n);
}

// Holidays — fixed palettes with optional base-color influence on first channel
function genChristmas(base, n) {
	return sceneLooks([
		[rgb(200, 0, 0), rgb(0, 120, 0), rgb(220, 180, 40), rgb(255, 240, 200)],
		[rgb(0, 120, 0), rgb(200, 0, 0), rgb(220, 180, 40), rgb(180, 0, 0)],
	], n);
}

function genHalloween(base, n) {
	return singleLook([rgb(255, 120, 0), rgb(120, 0, 180), rgb(20, 20, 20), rgb(200, 80, 0)], n);
}

function genValentines(base, n) {
	return singleLook([rgb(220, 20, 80), rgb(255, 150, 180), rgb(255, 220, 230), rgb(180, 0, 60)], n);
}

function genStPatricks(base, n) {
	return singleLook([rgb(0, 140, 60), rgb(0, 180, 80), rgb(220, 180, 40), rgb(0, 100, 40)], n);
}

function genJuly4(base, n) {
	return singleLook([rgb(200, 0, 0), rgb(255, 255, 255), rgb(0, 50, 150), rgb(200, 0, 0)], n);
}

function genHanukkah(base, n) {
	return singleLook([rgb(0, 60, 180), rgb(200, 210, 230), rgb(255, 220, 100), rgb(0, 80, 200)], n);
}

function genDiwali(base, n) {
	return sceneLooks([
		[rgb(255, 180, 0), rgb(255, 100, 0), rgb(255, 220, 150), rgb(200, 50, 150)],
		[rgb(255, 140, 0), rgb(255, 220, 100), rgb(255, 80, 0), rgb(180, 40, 120)],
	], n);
}

function genEaster(base, n) {
	return singleLook([rgb(255, 200, 220), rgb(200, 180, 255), rgb(255, 255, 180), rgb(180, 255, 220)], n);
}

function genNewYear(base, n) {
	return sceneLooks([
		[rgb(255, 215, 0), rgb(200, 200, 220), rgb(20, 20, 40), rgb(255, 240, 180)],
		[rgb(200, 200, 220), rgb(255, 215, 0), rgb(255, 255, 255), rgb(255, 200, 50)],
	], n);
}

function genThanksgiving(base, n) {
	return singleLook([rgb(200, 100, 30), rgb(140, 60, 20), rgb(220, 180, 100), rgb(180, 80, 40)], n);
}

function genPride(base, n) {
	return sceneLooks([
		[rgb(255, 0, 0), rgb(255, 140, 0), rgb(255, 240, 0), rgb(0, 180, 0)],
		[rgb(0, 100, 255), rgb(80, 0, 160), rgb(255, 0, 140), rgb(255, 100, 180)],
	], n);
}

function genChineseNewYear(base, n) {
	return singleLook([rgb(220, 0, 0), rgb(255, 215, 0), rgb(200, 0, 0), rgb(255, 180, 0)], n);
}

function genKwanzaa(base, n) {
	return singleLook([rgb(200, 0, 0), rgb(0, 100, 0), rgb(20, 20, 20), rgb(255, 200, 0)], n);
}

function genMardiGras(base, n) {
	return singleLook([rgb(120, 0, 180), rgb(0, 160, 60), rgb(255, 215, 0), rgb(160, 0, 200)], n);
}

function genOktoberfest(base, n) {
	return singleLook([rgb(0, 80, 180), rgb(255, 255, 255), rgb(220, 180, 40), rgb(0, 60, 140)], n);
}

function genJuneteenth(base, n) {
	return singleLook([rgb(200, 0, 0), rgb(20, 20, 20), rgb(0, 120, 0), rgb(255, 215, 0)], n);
}

function genRamadan(base, n) {
	return singleLook([rgb(0, 100, 80), rgb(255, 215, 0), rgb(0, 60, 50), rgb(220, 180, 100)], n);
}

function genDayOfDead(base, n) {
	return singleLook([rgb(255, 120, 0), rgb(200, 0, 100), rgb(255, 220, 0), rgb(120, 0, 80)], n);
}

function genCanadaDay(base, n) {
	return singleLook([rgb(220, 0, 0), rgb(255, 255, 255), rgb(220, 0, 0), rgb(200, 0, 0)], n);
}

function genAustraliaDay(base, n) {
	return singleLook([rgb(0, 50, 150), rgb(220, 0, 0), rgb(255, 255, 255), rgb(0, 80, 180)], n);
}

function genCincoDeMayo(base, n) {
	return singleLook([rgb(0, 120, 60), rgb(220, 0, 0), rgb(255, 255, 255), rgb(255, 200, 0)], n);
}

function genMemorialDay(base, n) {
	return singleLook([rgb(200, 0, 0), rgb(255, 255, 255), rgb(0, 50, 150), rgb(180, 0, 0)], n);
}

function genVeteransDay(base, n) {
	return singleLook([rgb(200, 0, 0), rgb(255, 255, 255), rgb(0, 50, 100), rgb(220, 180, 40)], n);
}

function genMothersDay(base, n) {
	return singleLook([rgb(255, 180, 200), rgb(255, 220, 230), rgb(220, 140, 180), rgb(255, 200, 220)], n);
}

function genFathersDay(base, n) {
	return singleLook([rgb(60, 100, 160), rgb(80, 120, 180), rgb(200, 180, 120), rgb(40, 70, 120)], n);
}

function genEarthDay(base, n) {
	return singleLook([rgb(0, 140, 60), rgb(100, 180, 80), rgb(0, 100, 180), rgb(60, 140, 40)], n);
}

function genGraduation(base, n) {
	return singleLook([rgb(20, 20, 60), rgb(255, 215, 0), rgb(255, 255, 255), rgb(200, 170, 40)], n);
}

function genWedding(base, n) {
	return singleLook([rgb(255, 250, 245), rgb(255, 230, 220), rgb(255, 215, 200), rgb(240, 220, 210)], n);
}

function genBirthday(base, n) {
	return sceneLooks([
		[rgb(255, 100, 150), rgb(100, 200, 255), rgb(255, 220, 80), rgb(180, 120, 255)],
		[rgb(100, 220, 150), rgb(255, 150, 100), rgb(255, 100, 200), rgb(120, 180, 255)],
	], n);
}

const GENERATORS = {
	complementary: genComplementary,
	split_complementary: genSplitComplementary,
	triadic: genTriadic,
	tetradic: genTetradic,
	analogous: genAnalogous,
	accented_analogous: genAccentedAnalogous,
	monochromatic: genMonochromatic,
	monochromatic_deep: genMonochromaticDeep,
	mccandless_warm_cool: genWarmCoolMcCandless,
	high_key: genHighKey,
	low_key: genLowKey,
	front_warm_back_cool: genFrontWarmBackCool,
	amber_wash_blue_back: genAmberWashBlueBack,
	sidelight_contrast: genSidelightContrast,
	gel_3200k: genGel3200K,
	gel_5600k: genGel5600K,
	fire: genFire,
	ocean: genOcean,
	sunset: genSunset,
	forest: genForest,
	moonlight: genMoonlight,
	candlelight: genCandlelight,
	neon_cyberpunk: genNeonCyberpunk,
	aurora: genAurora,
	storm: genStorm,
	royal: genRoyal,
	spring_garden: genSpringGarden,
	autumn_harvest: genAutumnHarvest,
	winter_frost: genWinterFrost,
	underwater: genUnderwater,
	desert_dusk: genDesertDusk,
	lava: genLava,
	ice_palace: genIcePalace,
	tropical: genTropical,
	blacklight: genBlacklight,
	sepia_vintage: genSepiaVintage,
	pastel_dream: genPastelDream,
	bold_primary: genBoldPrimary,
	dawn: genDawn,
	golden_hour: genGoldenHour,
	blue_hour: genBlueHour,
	twilight: genTwilight,
	midday_sun: genMiddaySun,
	overcast: genOvercast,
	rain: genRain,
	lightning_storm: genLightningStorm,
	fog_mist: genFogMist,
	snowfall: genSnowfall,
	blizzard: genBlizzard,
	jungle: genJungle,
	swamp: genSwamp,
	savanna: genSavanna,
	cave: genCave,
	volcanic_ash: genVolcanicAsh,
	deep_space: genDeepSpace,
	starlight: genStarlight,
	industrial: genIndustrial,
	sodium_vapor: genSodiumVapor,
	fluorescent: genFluorescent,
	clinical: genClinical,
	nightclub: genNightclub,
	concert_stage: genConcertStage,
	horror: genHorror,
	haunted: genHaunted,
	scifi_bridge: genSciFiBridge,
	fantasy_enchanted: genFantasyEnchanted,
	underworld: genUnderworld,
	ethereal: genEthereal,
	apocalypse: genApocalypse,
	bioluminescence: genBioluminescence,
	coral_reef: genCoralReef,
	arctic_tundra: genArcticTundra,
	mediterranean: genMediterranean,
	cherry_blossom: genCherryBlossom,
	wine_cellar: genWineCellar,
	lantern_festival: genLanternFestival,
	campfire: genCampfire,
	smog_haze: genSmogHaze,
	wizard_study: genWizardStudy,
	abandoned_warehouse: genAbandonedWarehouse,
	emerald_city: genEmeraldCity,
	infrared: genInfrared,
	rave_strobe: genRaveStrobe,
	deep_ocean: genDeepOcean,
	shallow_pool: genShallowPool,
	marshland: genMarshland,
	high_noon_desert: genHighNoonDesert,
	northern_woods: genNorthernWoods,
	copper_mine: genCopperMine,
	steel_mill: genSteelMill,
	traffic_signal: genTrafficSignal,
	police_siren: genPoliceSiren,
	television_glow: genTelevisionGlow,
	moody_noir: genMoodyNoir,
	romantic_dim: genRomanticDim,
	zen_garden: genZenGarden,
	hot_spring: genHotSpring,
	christmas: genChristmas,
	halloween: genHalloween,
	valentines: genValentines,
	st_patrick: genStPatricks,
	july_4: genJuly4,
	hanukkah: genHanukkah,
	diwali: genDiwali,
	easter: genEaster,
	new_year: genNewYear,
	thanksgiving: genThanksgiving,
	pride: genPride,
	chinese_new_year: genChineseNewYear,
	kwanzaa: genKwanzaa,
	mardi_gras: genMardiGras,
	oktoberfest: genOktoberfest,
	juneteenth: genJuneteenth,
	ramadan: genRamadan,
	day_of_dead: genDayOfDead,
	canada_day: genCanadaDay,
	australia_day: genAustraliaDay,
	cinco_de_mayo: genCincoDeMayo,
	memorial_day: genMemorialDay,
	veterans_day: genVeteransDay,
	mothers_day: genMothersDay,
	fathers_day: genFathersDay,
	earth_day: genEarthDay,
	graduation: genGraduation,
	wedding: genWedding,
	birthday: genBirthday,
	...SPORTS_GENERATORS,
};

export const EXPERT_CATEGORIES = [
	{
		id: 'color_theory',
		label: 'Color Theory',
		methods: [
			{ id: 'complementary', label: 'Complementary', description: 'Base + opposite hue (180°) for strong contrast.' },
			{ id: 'split_complementary', label: 'Split Complementary', description: 'Base + two hues adjacent to its complement.' },
			{ id: 'triadic', label: 'Triadic', description: 'Three hues evenly spaced 120° apart.' },
			{ id: 'tetradic', label: 'Tetradic (Square)', description: 'Four hues at 90° intervals on the wheel.' },
			{ id: 'analogous', label: 'Analogous', description: 'Neighboring hues within ~60° for harmony.' },
			{ id: 'accented_analogous', label: 'Accented Analogous', description: 'Analogous set plus one complementary accent.' },
			{ id: 'monochromatic', label: 'Monochromatic (Light)', description: 'One hue with lighter tints for even wash.' },
			{ id: 'monochromatic_deep', label: 'Monochromatic (Deep)', description: 'One hue with darker shades for depth.' },
		],
	},
	{
		id: 'lighting_design',
		label: 'Lighting Design',
		methods: [
			{ id: 'mccandless_warm_cool', label: 'McCandless Warm / Cool', description: 'Classic warm key with cool fill/back inspired by McCandless.' },
			{ id: 'high_key', label: 'High Key', description: 'Bright, low-contrast wash for cheerful open scenes.' },
			{ id: 'low_key', label: 'Low Key', description: 'Dark, moody wash with restrained luminance.' },
			{ id: 'front_warm_back_cool', label: 'Front Warm / Back Cool', description: 'Warm front fixtures, cool rear depth separation.' },
			{ id: 'amber_wash_blue_back', label: 'Amber Wash + Blue Back', description: 'Theatrical amber stage wash with blue backlight.' },
			{ id: 'sidelight_contrast', label: 'Sidelight Contrast', description: 'Alternating light/dark from opposing sides.' },
			{ id: 'gel_3200k', label: 'Tungsten (3200K)', description: 'Warm tungsten gel approximation.' },
			{ id: 'gel_5600k', label: 'Daylight (5600K)', description: 'Cool daylight gel approximation.' },
		],
	},
	{
		id: 'mood_environment',
		label: 'Mood & Environment',
		methods: [
			{ id: 'fire', label: 'Fire', description: 'Animated flicker cycle through reds and ambers.', cycle: true },
			{ id: 'ocean', label: 'Ocean', description: 'Deep blues and teals like underwater light.' },
			{ id: 'sunset', label: 'Sunset', description: 'Cycle from gold through magenta to twilight blue.', cycle: true },
			{ id: 'forest', label: 'Forest', description: 'Greens and earth tones for natural foliage.' },
			{ id: 'moonlight', label: 'Moonlight', description: 'Cool desaturated blues for night exteriors.' },
			{ id: 'candlelight', label: 'Candlelight', description: 'Warm amber intimate interior glow.' },
			{ id: 'neon_cyberpunk', label: 'Neon / Cyberpunk', description: 'Saturated magenta, cyan, and violet.' },
			{ id: 'aurora', label: 'Aurora Borealis', description: 'Slow cycle through green, teal, and violet.', cycle: true },
			{ id: 'storm', label: 'Storm', description: 'Muted gray-violet turbulent sky palette.' },
			{ id: 'royal', label: 'Royal', description: 'Regal purple with gold accents.' },
			{ id: 'spring_garden', label: 'Spring Garden', description: 'Soft greens and floral pastels.' },
			{ id: 'autumn_harvest', label: 'Autumn Harvest', description: 'Orange, rust, and golden brown.' },
			{ id: 'winter_frost', label: 'Winter Frost', description: 'Icy pale blues and whites.' },
			{ id: 'underwater', label: 'Underwater', description: 'Filtered aqua-teal subsea tones.' },
			{ id: 'desert_dusk', label: 'Desert Dusk', description: 'Warm terracotta fading to dusty violet.' },
			{ id: 'lava', label: 'Lava', description: 'Pulsing red-orange molten cycle.', cycle: true },
			{ id: 'ice_palace', label: 'Ice Palace', description: 'Crystalline cool whites and blues.' },
			{ id: 'tropical', label: 'Tropical', description: 'Turquoise, sunshine yellow, and coral.' },
			{ id: 'blacklight', label: 'Blacklight / UV', description: 'Fluorescent violet-green party palette.' },
			{ id: 'sepia_vintage', label: 'Sepia Vintage', description: 'Warm brown nostalgic film tones.' },
			{ id: 'pastel_dream', label: 'Pastel Dream', description: 'Soft low-saturation candy hues.' },
			{ id: 'bold_primary', label: 'Bold Primary', description: 'Pure red, green, blue theatrical primaries.' },
			{ id: 'dawn', label: 'Dawn', description: 'Pre-sunrise cycle from violet through rose to pale blue.', cycle: true },
			{ id: 'golden_hour', label: 'Golden Hour', description: 'Warm horizontal sunlight before sunset.' },
			{ id: 'blue_hour', label: 'Blue Hour', description: 'Cool twilight with a warm horizon accent.' },
			{ id: 'twilight', label: 'Twilight', description: 'Fading sunset into deep blue night.', cycle: true },
			{ id: 'midday_sun', label: 'Midday Sun', description: 'Bright neutral warm white daylight wash.' },
			{ id: 'overcast', label: 'Overcast Sky', description: 'Flat diffused gray-blue cloudy day.' },
			{ id: 'rain', label: 'Rain', description: 'Cool dim gray-blue wet atmosphere.', cycle: true },
			{ id: 'lightning_storm', label: 'Lightning Storm', description: 'Dark sky with intermittent white lightning flashes.', cycle: true },
			{ id: 'fog_mist', label: 'Fog / Mist', description: 'Pale desaturated gray-cyan haze.' },
			{ id: 'snowfall', label: 'Snowfall', description: 'Soft drifting pale blue-white cycle.', cycle: true },
			{ id: 'blizzard', label: 'Blizzard', description: 'Harsh bright white and cold gray snow.' },
			{ id: 'jungle', label: 'Jungle', description: 'Deep saturated tropical greens.' },
			{ id: 'swamp', label: 'Swamp / Bayou', description: 'Murky olive-brown stagnant water tones.' },
			{ id: 'savanna', label: 'Savanna', description: 'Golden grassland and warm amber sky.' },
			{ id: 'cave', label: 'Cave / Torchlight', description: 'Dark void with warm flickering torch amber.' },
			{ id: 'volcanic_ash', label: 'Volcanic Ash', description: 'Smoky gray with ember-red undertones.' },
			{ id: 'deep_space', label: 'Deep Space', description: 'Near-black with indigo and violet nebula tones.' },
			{ id: 'starlight', label: 'Starlight', description: 'Dark blue night sky with subtle cool depth.' },
			{ id: 'deep_ocean', label: 'Deep Ocean', description: 'Abyssal navy and midnight blue depths.' },
			{ id: 'shallow_pool', label: 'Shallow Pool', description: 'Bright aqua-turquoise clear water.' },
			{ id: 'marshland', label: 'Marshland', description: 'Muted olive and brown wetland tones.' },
			{ id: 'high_noon_desert', label: 'High Noon Desert', description: 'Bleached sand and harsh warm sun.' },
			{ id: 'northern_woods', label: 'Northern Woods', description: 'Cool deep pine and boreal forest green.' },
			{ id: 'industrial', label: 'Industrial', description: 'Sodium amber with steel gray factory tones.' },
			{ id: 'sodium_vapor', label: 'Sodium Vapor Street', description: 'Orange-yellow streetlamp wash.' },
			{ id: 'fluorescent', label: 'Fluorescent Office', description: 'Harsh green-tinted overhead tubes.' },
			{ id: 'clinical', label: 'Clinical / Hospital', description: 'Cold sterile blue-white examination light.' },
			{ id: 'copper_mine', label: 'Copper Mine', description: 'Earthy rust and oxidized copper tones.' },
			{ id: 'steel_mill', label: 'Steel Mill', description: 'Molten orange against iron gray.', cycle: true },
			{ id: 'smog_haze', label: 'Smog / Haze', description: 'Brown-gray urban air pollution muted wash.' },
			{ id: 'nightclub', label: 'Nightclub', description: 'Rotating saturated dance-floor colors.', cycle: true },
			{ id: 'concert_stage', label: 'Concert Stage', description: 'Red rock wash with warm haze.', cycle: true },
			{ id: 'rave_strobe', label: 'Rave Strobe', description: 'Dark room with white strobe flashes.', cycle: true },
			{ id: 'traffic_signal', label: 'Traffic Signal', description: 'Sequential red, amber, green theatrical gag.', cycle: true },
			{ id: 'police_siren', label: 'Police Siren', description: 'Alternating red and blue emergency flash.', cycle: true },
			{ id: 'television_glow', label: 'Television Glow', description: 'Flickering cool gray-blue screen light in a dark room.', cycle: true },
			{ id: 'horror', label: 'Horror', description: 'Sickly green with blood-red accents.' },
			{ id: 'haunted', label: 'Haunted', description: 'Desaturated green-violet uneasy palette.', cycle: true },
			{ id: 'moody_noir', label: 'Film Noir', description: 'Deep shadow with minimal silvery fill.' },
			{ id: 'scifi_bridge', label: 'Sci-Fi Bridge', description: 'Teal console and instrument panel blues.' },
			{ id: 'fantasy_enchanted', label: 'Enchanted Forest', description: 'Purple, gold, and fairy-light sparkle.', cycle: true },
			{ id: 'wizard_study', label: "Wizard's Study", description: 'Deep purple shelves with candle amber.' },
			{ id: 'emerald_city', label: 'Emerald City', description: 'Vivid green architecture with silver highlights.' },
			{ id: 'underworld', label: 'Underworld', description: 'Hellish red-orange on near black.' },
			{ id: 'ethereal', label: 'Ethereal / Heaven', description: 'Soft white, gold, and blush pink glow.' },
			{ id: 'apocalypse', label: 'Post-Apocalypse', description: 'Dust, rust, smoke, and burnt orange.' },
			{ id: 'bioluminescence', label: 'Bioluminescence', description: 'Glowing cyan-green in dark water.', cycle: true },
			{ id: 'coral_reef', label: 'Coral Reef', description: 'Vibrant coral pink, turquoise, and orange.' },
			{ id: 'arctic_tundra', label: 'Arctic Tundra', description: 'Pale ice blue and frozen white expanse.' },
			{ id: 'mediterranean', label: 'Mediterranean Coast', description: 'Azure sea, white stone, terracotta roof.' },
			{ id: 'cherry_blossom', label: 'Cherry Blossom', description: 'Soft pink petals with spring green.' },
			{ id: 'wine_cellar', label: 'Wine Cellar', description: 'Deep burgundy with warm candle amber.' },
			{ id: 'lantern_festival', label: 'Lantern Festival', description: 'Warm pulsing paper-lantern amber glow.', cycle: true },
			{ id: 'campfire', label: 'Campfire', description: 'Intimate amber-red fire pit flicker.', cycle: true },
			{ id: 'abandoned_warehouse', label: 'Abandoned Warehouse', description: 'Single sickly green-gray bare bulb.' },
			{ id: 'infrared', label: 'Infrared / Thermal', description: 'False-color heat map reds and hot spots.' },
			{ id: 'romantic_dim', label: 'Romantic Dim', description: 'Deep rose and warm pink low light.' },
			{ id: 'zen_garden', label: 'Zen Garden', description: 'Muted stone gray and soft moss green.' },
			{ id: 'hot_spring', label: 'Hot Spring / Steam', description: 'Soft mineral teal with rising steam cycle.', cycle: true },
		],
	},
	{
		id: 'holidays',
		label: 'Holidays & Events',
		methods: [
			{ id: 'christmas', label: 'Christmas', description: 'Red, green, and gold alternating cycle.', cycle: true },
			{ id: 'halloween', label: 'Halloween', description: 'Orange, purple, and black.' },
			{ id: 'valentines', label: "Valentine's Day", description: 'Red, pink, and blush white.' },
			{ id: 'st_patrick', label: "St. Patrick's Day", description: 'Irish green and gold.' },
			{ id: 'july_4', label: 'Fourth of July (US)', description: 'Red, white, and blue.' },
			{ id: 'hanukkah', label: 'Hanukkah', description: 'Blue, silver, and gold.' },
			{ id: 'diwali', label: 'Diwali', description: 'Gold and amber festival cycle.', cycle: true },
			{ id: 'easter', label: 'Easter', description: 'Pastel pink, lavender, and yellow.' },
			{ id: 'new_year', label: "New Year's Eve", description: 'Gold, silver, and midnight sparkle.', cycle: true },
			{ id: 'thanksgiving', label: 'Thanksgiving (US)', description: 'Amber, brown, and harvest gold.' },
			{ id: 'pride', label: 'Pride', description: 'Rainbow cycle through spectrum hues.', cycle: true },
			{ id: 'chinese_new_year', label: 'Chinese New Year', description: 'Red and gold prosperity colors.' },
			{ id: 'kwanzaa', label: 'Kwanzaa', description: 'Red, black, and green with gold.' },
			{ id: 'mardi_gras', label: 'Mardi Gras', description: 'Purple, green, and gold.' },
			{ id: 'oktoberfest', label: 'Oktoberfest', description: 'Bavarian blue and white with gold.' },
			{ id: 'juneteenth', label: 'Juneteenth', description: 'Red, black, and green with gold.' },
			{ id: 'ramadan', label: 'Ramadan / Eid', description: 'Teal, gold, and warm lantern amber.' },
			{ id: 'day_of_dead', label: 'Día de los Muertos', description: 'Marigold orange, magenta, and gold.' },
			{ id: 'canada_day', label: 'Canada Day', description: 'Red and white.' },
			{ id: 'australia_day', label: 'Australia Day', description: 'Blue, red, and white.' },
			{ id: 'cinco_de_mayo', label: 'Cinco de Mayo', description: 'Green, red, and white with gold.' },
			{ id: 'memorial_day', label: 'Memorial Day (US)', description: 'Red, white, and blue tribute.' },
			{ id: 'veterans_day', label: 'Veterans Day (US)', description: 'Red, white, blue, and gold honor.' },
			{ id: 'mothers_day', label: "Mother's Day", description: 'Soft pink and rose tones.' },
			{ id: 'fathers_day', label: "Father's Day", description: 'Navy, slate, and warm tan.' },
			{ id: 'earth_day', label: 'Earth Day', description: 'Green and blue environmental palette.' },
			{ id: 'graduation', label: 'Graduation', description: 'School colors: navy and gold.' },
			{ id: 'wedding', label: 'Wedding', description: 'Warm ivory and blush white wash.' },
			{ id: 'birthday', label: 'Birthday Party', description: 'Festive multicolor cycle.', cycle: true },
		],
	},
	...SPORTS_CATEGORIES,
];

export function getExpertMethod(methodId) {
	for (const cat of EXPERT_CATEGORIES) {
		if (cat.groups) {
			for (const group of cat.groups) {
				const method = group.methods.find((m) => m.id === methodId);
				if (method) return { ...method, category: cat.id, region: group.label, groupId: group.id };
			}
		}
		if (cat.methods) {
			const method = cat.methods.find((m) => m.id === methodId);
			if (method) return { ...method, category: cat.id };
		}
	}
	return null;
}

export function getExpertCategoryGroups(categoryId) {
	const cat = EXPERT_CATEGORIES.find((c) => c.id === categoryId);
	return cat?.groups || [];
}

export function getExpertCategoryGroup(categoryId, groupId) {
	const cat = EXPERT_CATEGORIES.find((c) => c.id === categoryId);
	return cat?.groups?.find((g) => g.id === groupId) || null;
}

export function findExpertGroupIdForMethod(categoryId, methodId) {
	const cat = EXPERT_CATEGORIES.find((c) => c.id === categoryId);
	for (const group of cat?.groups || []) {
		if (group.methods.some((m) => m.id === methodId)) return group.id;
	}
	return cat?.groups?.[0]?.id || null;
}

export function runExpertMethod(methodId, baseColor, numColors) {
	const gen = GENERATORS[methodId];
	if (!gen) return null;
	const result = gen(baseColor || rgb(128, 128, 128), numColors || 4);
	if (result.type === 'look') {
		return { ...result, colors: fitCount(result.colors, numColors || 4) };
	}
	if (result.type === 'cycle') {
		return {
			...result,
			looks: result.looks.map((l) => ({
				...l,
				colors: fitCount(l.colors, numColors || 4),
			})),
		};
	}
	return null;
}

export function rgbToHex({ r, g, b }) {
	const toHex = (v) => clamp(v, 0, 255).toString(16).padStart(2, '0');
	return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function hexToRgb(hex) {
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	return result
		? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) }
		: { r: 128, g: 128, b: 128 };
}
